// 쓰는 도중에 배포가 나갔을 때 뜨는 알림 한 줄.
//
// 머리말 뱃지는 "언젠가 보면 되는 것"이라 눈에 안 들어온다. 반면 **방금 화면이 바뀐 순간**에는
// 알려주는 편이 낫다 — 안 그러면 "어? 아까랑 다른데?" 하고 당황하거나, 새로고침 전까지 옛 화면을
// 계속 본다. 그래서 세션 도중에 새 소식이 도착한 경우에만 잠깐 띄우고, 누르면 소식 화면으로 간다.
//
// **처음 들어온 순간에는 절대 안 뜬다** — 그건 "새 소식"이 아니라 그냥 지난 소식이다
// (판정은 useWhatsNew 의 세션 기준 시각이 한다).

import { useEffect, useState } from "react";
import { useModal } from "../../hooks/useModal";
import { useWhatsNew } from "../../hooks/useWhatsNew";

const AUTO_HIDE_MS = 15_000;

export default function WhatsNewToast() {
  const { arrived, markAllSeen } = useWhatsNew();
  const { openModal } = useModal("whats-new");
  const [dismissed, setDismissed] = useState<string | null>(null);

  const top = arrived[0] ?? null;
  const show = !!top && dismissed !== top.id;

  // 계속 떠 있으면 화면을 가린다 — 한참 지나면 스스로 물러난다(뱃지는 그대로 남는다).
  useEffect(() => {
    if (!show || !top) return;
    const t = setTimeout(() => setDismissed(top.id), AUTO_HIDE_MS);
    return () => clearTimeout(t);
  }, [show, top]);

  if (!show || !top) return null;

  return (
    <div className="fixed bottom-5 left-1/2 z-[99998] w-[min(92vw,26rem)] -translate-x-1/2">
      <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white p-3.5 shadow-lg dark:border-gray-700 dark:bg-gray-900">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-400">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4.5 w-4.5">
            <path d="M3 11v3a1 1 0 0 0 1 1h2l4 3V8L6 11H4a1 1 0 0 0-1 1z" />
            <path d="M15 8.5a4 4 0 0 1 0 7" />
          </svg>
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-brand-600 dark:text-brand-400">방금 업데이트됐어요</p>
          <p className="truncate text-sm font-medium text-gray-800 dark:text-white/90">{top.title}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            openModal();
            markAllSeen();
            setDismissed(top.id);
          }}
          className="shrink-0 whitespace-nowrap rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-600"
        >
          확인하기
        </button>
        <button
          type="button"
          onClick={() => setDismissed(top.id)}
          aria-label="닫기"
          className="shrink-0 rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-4 w-4">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
